import type { OrgConfigPublic } from '@lms/shared-types'
import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useEffect, type ReactNode } from 'react'

import { getPublicOrgConfig } from '../api/org-config'
import { applyDocumentBranding } from './branding'
import { applyTheme } from './apply-theme'
import { flags } from './feature-flags'

const DEFAULT_ORG_CONFIG: OrgConfigPublic = {
  companyName: 'YTL AI Labs',
  companyShortName: 'Leave Hub',
  appName: 'Leave Hub',
  logoLightUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  theme: {},
}

const OrgConfigContext = createContext<OrgConfigPublic>(DEFAULT_ORG_CONFIG)

export function OrgConfigProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({
    queryKey: ['org-config', 'public'],
    queryFn: getPublicOrgConfig,
    staleTime: 5 * 60_000,
  })

  // Apply runtime theme + document branding (tab title / favicon) whenever
  // the config loads or changes. Gated by the kill-switch flag; when off we
  // leave the static tokens.css palette + index.html title in place.
  useEffect(() => {
    if (!flags.whiteLabelChrome || !data) return
    applyTheme(data.theme)
    applyDocumentBranding({ appName: data.appName, faviconUrl: data.faviconUrl })
  }, [data])

  return (
    <OrgConfigContext.Provider value={data ?? DEFAULT_ORG_CONFIG}>
      {children}
    </OrgConfigContext.Provider>
  )
}

export function useOrgConfig(): OrgConfigPublic {
  return useContext(OrgConfigContext)
}
